import { remove, sortBy } from 'lodash-es';
import localStore from 'store';

import { dashboardBffClient } from '@/api-client';
import type { Event as BackendEvent } from '@/api-client/api';
import { useAppInfoStore } from '@/stores/appInfo';
import { useLicensingStore } from '@/stores/licensing';
import { useMultiClusterStore } from '@/stores/multiCluster';

import constants from './constants';
import { error as logError } from './logger';
import { policyProfiles } from './pages/policies/policy-utils';
import { defaultErrorHandler, fetchPolicies } from './services';
import { useAccessRightsStore } from './stores/accessRights/accessRights';
import { type AlertMessage, useMainStore } from './stores/main';
import { useProfileStore } from './stores/profiles/profiles';
import { isK10DrEnabled } from './utils/fetch-utils';
import {
  getGlobalLocationProfileByName,
  getImmutableProfileNames,
  getLocationProfileByName,
} from './utils/profile-utils';

// muted is derived from localStorage inside addAlertMessage, not supplied by callers.
type PartialAlertMessage = Omit<AlertMessage, 'firstSeen' | 'lastSeen' | 'muted'>;

// Local list - Used temporarily and then copied to Pinia to avoid flashing in the UI.
let messages: AlertMessage[] = [];

/** @internal — exposed for unit tests only. Returns a snapshot so tests can't mutate module state. */
export const _messagesForTesting = (): AlertMessage[] => [...messages];
/** @internal — exposed for unit tests only */
export const _resetMessagesForTesting = (): void => {
  messages = [];
};

export function localStoreKey(alert: PartialAlertMessage): string {
  return `'k10.alert-muted.'${alert.id}`;
}

/**
 * Add an alert to the list.
 * Mute the message if it's recorded as muted in localStorage.
 */
// TODO: We probably need to create an imperative API to manipulate alert messages stack from anywhere
function addAlertMessage(alert: PartialAlertMessage): void {
  const muted = localStore.get(localStoreKey(alert));
  const now = new Date().toISOString();
  messages.push({ firstSeen: now, lastSeen: now, ...alert, muted: !!muted });
}

/** @internal — exported so unit tests can invoke individual checks. */
export const checks = {
  async checkForPoliciesWithMissingProfiles() {
    const store = useMainStore();
    const canListProfiles = await useAccessRightsStore().waitForAccessRight('list', 'profiles');
    if (!canListProfiles) return;
    (store.policies ?? []).forEach((policy) => {
      const policyName = policy.metadata?.name;
      if (!policyName) return;
      const profiles = policyProfiles(policy);
      profiles.forEach((profileName) => {
        // If the policy profile cannot be found, create an alert
        if (
          !getLocationProfileByName(profileName) &&
          !getGlobalLocationProfileByName(profileName)
        ) {
          addAlertMessage({
            id: `policy-missing-profile-${policyName}-${profileName}`,
            level: constants.alertLevel.ERROR,
            title: `Policy missing profile`,
            message: `The policy <b>${policyName}</b> is dependent on a profile, <b>${profileName}</b>, that cannot be found.`,
            button: {
              text: `Go to Policies`,
              routeParams: { name: 'Policies', params: { action: 'filter', name: policyName } },
            },
          });
        }
      });
    });
  },
  async checkForK10DrNotEnabled() {
    const isAdmin = await useAccessRightsStore().waitForIsAdmin();
    if (!isAdmin) return;

    const enabled = await isK10DrEnabled();
    if (!enabled) {
      addAlertMessage({
        id: `k10-dr-not-enabled`,
        level: constants.alertLevel.WARNING,
        title: `Kasten DR is disabled`,
        message:
          'Kasten Disaster Recovery (KDR) is not enabled. Enabling KDR is required to protect the local restore point catalog and ensure exported backup data is recoverable in the event of cluster failure.',
        button: {
          text: `Go to Kasten DR Setup`,
          routeParams: { name: 'DRSetup' },
        },
      });
    }
  },
  async checkForUpgradeAvailable() {
    const appInfoStore = useAppInfoStore();
    if (!appInfoStore.upgradeInfo?.latestVersion) {
      await appInfoStore
        .fetchUpgradeInfo()
        .catch((err) => logError('error fetching upgradeInfo in checkForUpgradeAvailable', err));
    }
    const currentVersion = appInfoStore.upgradeInfo?.currentVersion;
    const latestVersion = appInfoStore.upgradeInfo?.latestVersion;
    if (!currentVersion || !latestVersion) return;
    if (currentVersion !== latestVersion) {
      addAlertMessage({
        id: `k10-upgrade-available-${latestVersion}`,
        level: constants.alertLevel.INFO,
        title: `Upgrade Available • ${latestVersion}`,
        message: `You're currently running <b>v${currentVersion}</b> of Kasten. Version <b>${latestVersion}</b> is available.`,
        button: {
          text: `See Kasten Upgrade Docs`,
          href: `https://docs.kasten.io/${latestVersion}/install/upgrade`,
        },
      });
    }
  },
  async checkForDRWithoutImmutableProfile() {
    const store = useMainStore();
    const appInfoStore = useAppInfoStore();
    const isAdmin = await useAccessRightsStore().waitForIsAdmin();
    if (!isAdmin) return;
    const drPolicy = store.policies?.find(
      (p) =>
        p.metadata?.name === constants.K10_DR_POLICY_NAME &&
        p.metadata?.namespace === appInfoStore.systemNamespace,
    );
    // Bail if DR isn't enabled
    if (!drPolicy) return;

    const buProfileName = drPolicy?.spec?.actions?.[0]?.backupParameters?.profile?.name;
    const immutProfileNames = getImmutableProfileNames();
    // If immutable profiles exist, but DR is not using one of them, create an alert.
    if (
      immutProfileNames.length &&
      buProfileName !== undefined &&
      !immutProfileNames.includes(buProfileName)
    ) {
      addAlertMessage({
        id: `k10-dr-not-immutable-${buProfileName}`,
        level: constants.alertLevel.WARNING,
        title: `Kasten DR is not immutable`,
        message:
          'Kasten Disaster Recovery (KDR) is not configured to export to an immutable Location Profile. Using an immutable Location Profile is strongly recommended to increase restore point catalog resilience.',
        button: {
          text: `Go to Kasten DR Setup`,
          routeParams: { name: 'DRSetup' },
        },
      });
    }
  },
  checkForUnenforcedEnterpriseLicenseIssues() {
    const licensingStore = useLicensingStore();
    const supportMsg = `Contact Veeam Sales through your account contact or at <a target="_blank" href="https://www.veeam.com/contact-sales.html">https://www.veeam.com/contact-sales.html</a> for an updated license.`;
    const licenseStatus = licensingStore.licenseStatus;
    const licenseUsage = licensingStore.licenseUsage;

    if (
      licenseUsage !== null &&
      licenseUsage.nodesRequestedByClusters - licenseUsage.nodesContributedFromClusters > 0
    ) {
      addAlertMessage({
        id: `k10-mc-lease-overage`,
        level: constants.alertLevel.WARNING,
        title: `Multi-Cluster licensing limits have been exceeded`,
        message: `More nodes are being requested than are available from the license pool. ${supportMsg}`,
        button: {
          text: `View License Usage`,
          routeParams: { name: 'MCLicensing' },
        },
      });
    }
  },
  async checkForBackendEvents() {
    try {
      const { data: events } = await dashboardBffClient.bff.bffListEvents();
      events?.forEach((event) => {
        if (event.action in backendEventHandlerMap) {
          backendEventHandlerMap[event.action as keyof typeof backendEventHandlerMap](event);
        } else {
          addAlertMessage({
            id: event.id,
            title: event.title ?? '',
            message: event.message ?? '',
            level: event.level as PartialAlertMessage['level'],
            button: {
              text: `Dismiss Alert`,
              dismiss: true,
            },
          });
        }
      });
    } catch (error) {
      defaultErrorHandler(error);
    }
  },
  async checkPrimaryHasIngress() {
    const appInfoStore = useAppInfoStore();
    const multiClusterStore = useMultiClusterStore();
    if (!appInfoStore.isMCPrimary) return;
    // if a primary exists in the clusters list & it's ingress URL is not set show alert.
    if (
      multiClusterStore.primaryCluster &&
      !multiClusterStore.primaryCluster.spec?.k10?.ingress?.url
    ) {
      addAlertMessage({
        id: `k10-mc-primary-no-ingress`,
        level: constants.alertLevel.ERROR,
        title: `The primary cluster's ingress is not set`,
        message: `An ingress URL must be explicitly set for your primary cluster or Multi-Cluster features will not work properly.`,
        button: {
          text: `See Multi-Cluster Docs`,
          href: `https://docs.kasten.io/${appInfoStore.systemVersion}/multicluster/upgrading`,
        },
      });
    }
  },
  checkForK10DRWithNoKanisterProfile() {
    // TODO
  },
  checkforDistributionErrors() {
    // TODO
  },
};

/**
 * Contains specific alerts for backend actions
 */
const backendEventHandlerMap = {
  runActionFailure: (event: BackendEvent) => {
    const params = JSON.parse(event.message ?? '{}');
    const path = 'runaction' in params ? `policy-run/${params.runaction}` : 'dashboard';
    const { kind, uid, name, namespace } = params;
    const query = Object.entries({ displayAction: true, kind, uid, name, namespace })
      .map(([key, value]) => key + '=' + encodeURIComponent(value as string))
      .join('&');
    addAlertMessage({
      id: event.id,
      title: event.title ?? '',
      level: event.level as PartialAlertMessage['level'],
      button: {
        text: `Dismiss Alert`,
        dismiss: true,
      },
      message: `<a target='_blank'rel='noopener noreferrer' href='${window.location.pathname.replace(
        /\/$/,
        '',
      )}/#/${path}?${query}' class='inline-flex items-center justify-center rounded font-semibold capitalize transition-colors disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 bg-transparent text-blue-500 hover:text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 gap-0 focus-visible:outline-none focus-visible:outline-transparent focus-visible:underline mt-2 p-0'>See the details</a>`,
    });
  },
};

/**
 * Executes all the checks. Called from polling.js on a 10 sec loop.
 */
export async function checkAll(): Promise<void> {
  const store = useMainStore();
  const licensingStore = useLicensingStore();
  const appInfoStore = useAppInfoStore();
  const profileStore = useProfileStore();
  if (store.xhrEnabled) {
    const reqs = [fetchPolicies(), profileStore.fetchProfiles(), licensingStore.fetchStatus()];
    // add check for MC lease usage if this is a primary cluster
    if (appInfoStore.isMCPrimary) {
      reqs.push(licensingStore.fetchUsage());
    }
    await Promise.all(reqs);
  }
  messages = [];
  await Promise.all(Object.keys(checks).map((check) => checks[check as keyof typeof checks]()));

  // Reorder list
  messages = sortBy(messages, 'id'); // To avoid arbitrary ordering among level groups
  messages = messages.concat(remove(messages, (m) => m.level === constants.alertLevel.WARNING));
  messages = messages.concat(remove(messages, (m) => m.level === constants.alertLevel.INFO));
  messages = messages.concat(remove(messages, (m) => m.muted));

  store.alertMessages = messages;
}
